/* @generated */

// ../src/typeahead/typeahead.tsx
import "focus-visible";

// ../src/typeahead/wrapper.tsx
import * as React2 from "react";
import { ClickOutside } from "@dropbox/dig-components/click_outside";

// ../src/_global/constants.ts
var ENTER_KEY = "Enter";
var SPACEBAR_KEY = " ";
var DOWN_ARROW_KEY = "ArrowDown";
var buttonDataAttributeName = "data-dig-button";
var buttonDataSelector = `[${buttonDataAttributeName}]`;
var buttonContentDataAttributeName = "data-dig-button-content";
var buttonContentDataSelector = `[${buttonContentDataAttributeName}]`;
var splitButtonDataAttributeName = "data-dig-split-button";
var splitButtonDataSelector = `[${splitButtonDataAttributeName}]`;
var iconButtonDataAttributeName = "data-dig-icon-button";
var iconButtonDataSelector = `[${iconButtonDataAttributeName}]`;

// ../src/typeahead/wrapper.tsx
import { useUniqueId } from "@dropbox/dig-components/hooks";
import { useMenu, returnFocus } from "@dropbox/dig-components/hooks";

// ../src/typeahead/constants.ts
var INTERACTIVE_TYPEAHEAD_ROW_CLASSNAME = "dig-Typeahead-row--interactive";
var INTERACTIVE_TYPEAHEAD_ROW_SELECTORS = (
  // interactive Typeahead.Rows
  [
    `.${INTERACTIVE_TYPEAHEAD_ROW_CLASSNAME}[role="option"]`,
    `.${INTERACTIVE_TYPEAHEAD_ROW_CLASSNAME}[role="menuitemcheckbox"]`,
    ".dig-Typeahead-results--show-more"
  ]
);

// ../src/typeahead/context.tsx
import * as React from "react";
import { createMenuRows } from "@dropbox/dig-components/hooks";
var defaultValue = {
  value: void 0,
  setSelected: () => false,
  setFocused: () => false,
  setSyntheticFocus: () => false,
  closeMenu: () => false,
  isPortaled: true,
  menuRows: createMenuRows(),
  containerId: "",
  setIsTypeaheadDropdownVisible: () => {
  },
  shouldOmitWrapperOverlay: false,
  setShouldOmitWrapperOverlay: () => {
  }
};
var TypeaheadContext = React.createContext(defaultValue);
var TypeaheadRowContext = React.createContext({});
var TypeaheadResultContext = React.createContext({});

// ../src/typeahead/wrapper.tsx
import { DIGContext, isFeatureLevelSupported } from "@dropbox/dig-foundations";
var Wrapper = ({
  children,
  isPortaled,
  closeOnSelection = true,
  inlineProps,
  openMenuOnFocus = true,
  containerId,
  hasMaxHeight = false,
  onSelection,
  onBlur,
  onToggle,
  shouldHighlightFirstRow = false,
  omitOverlay = false,
  wrapperRef,
  ...rest
}) => {
  const triggerRef = React2.useRef(null);
  const innerWrapperRef = React2.useRef(null);
  const [isTriggerMouseDown, setIsTriggerMouseDown] = React2.useState(false);
  const [isTypeaheadMenuVisible, _setIsTypeaheadDropdownVisible] = React2.useState(false);
  const contentRef = React2.useRef(null);
  const setFocusedRef = React2.useRef(null);
  const isMouseEventRef = React2.useRef(false);
  const generatedContainerId = useUniqueId({ prefix: "dig-Typeahead", idOverride: containerId });
  const { featureLevel } = React2.useContext(DIGContext);
  const isExperimental = isFeatureLevelSupported(featureLevel);
  const [shouldOmitWrapperOverlay, setShouldOmitWrapperOverlay] = React2.useState(omitOverlay);
  const setIsTypeaheadDropdownVisible = React2.useCallback(
    (isVisible) => {
      if (isTypeaheadMenuVisible !== isVisible) {
        _setIsTypeaheadDropdownVisible(isVisible);
      }
    },
    [isTypeaheadMenuVisible]
  );
  const localOnSelection = React2.useCallback(
    (value2, event) => {
      if (onSelection) {
        onSelection(value2, event);
      }
      if (closeOnSelection) {
        if (setFocusedRef && setFocusedRef.current) {
          setFocusedRef.current(void 0);
        }
        if (triggerRef) {
          returnFocus(triggerRef);
        }
      }
    },
    [closeOnSelection, onSelection, setFocusedRef]
  );
  const {
    isOpen,
    openMenu,
    closeMenu,
    toggleMenu,
    value,
    setSelected,
    setFocused,
    setSyntheticFocus,
    getRootProps,
    getTriggerProps,
    getContentProps,
    menuRows,
    isFocusing
  } = useMenu({
    triggerRef,
    contentRef,
    onSelection: localOnSelection,
    onToggle,
    closeOnSelection,
    shouldFocusDisabledRows: true,
    interactiveRowSelectors: INTERACTIVE_TYPEAHEAD_ROW_SELECTORS,
    // Disallow letter keys from navigating typeahead, they may be part of the query.
    allowLetterNavigation: false,
    // TAB shouldn't focus the menu, DOWN should.
    focusMenuRowTriggerKey: DOWN_ARROW_KEY,
    // SPACE keys shouldn't trigger the menu to open, they may be part of the query.
    allowSpaceBarToOpenMenu: false,
    // We need to set focus on next tick to allow state changes to flush.
    // Otherwise onBlur will close the menu too early.
    focusNextItemImmediately: false,
    shouldHighlightFirstRow
  });
  setFocusedRef.current = setFocused;
  React2.useImperativeHandle(
    wrapperRef,
    () => {
      return {
        focusTrigger: () => {
          const el = triggerRef.current;
          if (el && "focus" in el) {
            el.focus();
          }
        },
        openResults: (options2 = {}) => {
          openMenu(options2);
        },
        closeResults: (options2 = {}) => {
          closeMenu(options2);
        }
      };
    },
    [openMenu, closeMenu]
  );
  const resetTriggerMouseDown = React2.useCallback(() => {
    setIsTriggerMouseDown(false);
    document.removeEventListener("mouseup", resetTriggerMouseDown);
  }, []);
  React2.useEffect(() => {
    if (isTriggerMouseDown) {
      document.addEventListener("mouseup", resetTriggerMouseDown);
    }
    return () => document.removeEventListener("mouseup", resetTriggerMouseDown);
  }, [isTriggerMouseDown, resetTriggerMouseDown]);
  React2.useEffect(() => {
    const pointerDownListener = () => {
      isMouseEventRef.current = true;
    };
    if (isOpen && isExperimental) {
      document.addEventListener("pointerdown", pointerDownListener, { capture: true });
    }
    return () => document.removeEventListener("pointerdown", pointerDownListener, { capture: true });
  }, [isOpen, isExperimental]);
  React2.useEffect(() => {
    const keyDownListener = () => {
      isMouseEventRef.current = false;
    };
    if (isOpen && isExperimental) {
      document.addEventListener("keydown", keyDownListener, { capture: true });
    }
    return () => document.removeEventListener("keydown", keyDownListener, { capture: true });
  }, [isOpen, isExperimental]);
  const options = {
    getContentProps: (options2) => {
      return {
        open: Boolean(isOpen),
        triggerRef,
        ...getContentProps({ tabIndex: void 0, ...options2 }),
        // Override useMenu's role
        role: "listbox"
      };
    },
    getTriggerProps: (options2) => {
      const typeaheadTriggerKeyDown = (e) => {
        if (inlineProps) {
          const { customOpenKey, onCustomOpenKeyTrigger, customCloseKey } = inlineProps;
          if ("key" in customOpenKey && e.key === customOpenKey.key || "keys" in customOpenKey && customOpenKey.keys.includes(e.key)) {
            openMenu({ autoFocus: false });
            if (onCustomOpenKeyTrigger) onCustomOpenKeyTrigger(e);
          } else if (customCloseKey && e.key === customCloseKey.key) {
            closeMenu({ autoFocus: false });
          }
        }
        if (options2 && options2.onKeyDown) {
          options2.onKeyDown(e);
        }
      };
      const typeaheadTriggerOnChange = (e) => {
        if (!inlineProps) {
          openMenu({ autoFocus: false });
        }
        if (options2 && options2.onChange) {
          options2.onChange(e);
        }
      };
      const typeaheadTriggerOnMouseDown = (e) => {
        if (options2 && options2.onMouseDown) {
          options2.onMouseDown(e);
        }
        setIsTriggerMouseDown(true);
      };
      return {
        ...getTriggerProps({ ...options2, onKeyDown: typeaheadTriggerKeyDown }),
        /** Typeahead overrides */
        role: "combobox",
        "aria-haspopup": "listbox",
        "aria-autocomplete": "list",
        "aria-controls": generatedContainerId,
        onChange: typeaheadTriggerOnChange,
        onMouseDown: typeaheadTriggerOnMouseDown,
        onClick: options2 ? options2.onClick : void 0,
        onKeyUp: options2 ? options2.onKeyUp : void 0,
        onFocus: (e) => {
          if (openMenuOnFocus) openMenu({ autoFocus: false });
          setFocused(void 0);
          if (options2 && options2.onFocus) {
            options2.onFocus(e);
          }
        }
      };
    },
    openMenu,
    closeMenu,
    toggleMenu,
    triggerRef
  };
  const onTypeaheadBlur = React2.useCallback(
    (e) => {
      var _a;
      const isBlurFromMouse = isMouseEventRef.current === true;
      const focusIsMovingInsideOfWrapper = e.relatedTarget && ((_a = innerWrapperRef.current) == null ? void 0 : _a.contains(e.relatedTarget));
      if (!(value || isFocusing) && !focusIsMovingInsideOfWrapper && // Experimental. We want to close the menu if the blur is not from a mouse event.
      // We also want to close the menu if the menu is not visible. The net change here
      // is actual clicks on the ClickOutside are now handled by ClickOutside instead of
      // this blur event
      (!isExperimental || !isBlurFromMouse || !isTypeaheadMenuVisible)) {
        closeMenu({ autoFocus: false });
      }
      if (onBlur) {
        onBlur(e);
      }
    },
    [value, isFocusing, closeMenu, onBlur, isTypeaheadMenuVisible, isExperimental]
  );
  const defaultValue2 = {
    isPortaled,
    contentRef,
    value,
    setSelected,
    setFocused,
    setSyntheticFocus,
    closeMenu,
    inlineProps,
    menuRows,
    containerId: generatedContainerId,
    hasMaxHeight,
    setIsTypeaheadDropdownVisible,
    shouldHighlightFirstRow,
    shouldOmitWrapperOverlay,
    setShouldOmitWrapperOverlay
  };
  const handleClickOutside = React2.useCallback(() => {
    closeMenu({ autoFocus: false });
  }, [closeMenu]);
  const wrappedChildren = inlineProps ? (
    // eslint-disable-next-line react-compiler/react-compiler
    children && children(options)
  ) : /* @__PURE__ */ React2.createElement(
    ClickOutside,
    {
      className: "dig-Typeahead-backdrop",
      isBlock: true,
      onClickOutside: handleClickOutside,
      shouldPropagateMouseEvents: true,
      isClickThroughPortaled: isPortaled,
      isActive: Boolean(isOpen) && !isTriggerMouseDown && isTypeaheadMenuVisible
    },
    children && children(options)
  );
  return /* @__PURE__ */ React2.createElement(TypeaheadContext.Provider, { value: defaultValue2 }, /* @__PURE__ */ React2.createElement(
    "div",
    {
      className: "dig-Typeahead",
      ref: innerWrapperRef,
      ...getRootProps({
        ...rest,
        onBlur: onTypeaheadBlur
      })
    },
    wrappedChildren
  ));
};
Wrapper.displayName = "Wrapper";

// ../src/typeahead/row.tsx
import classNames5 from "classnames";
import * as React7 from "react";
import { useMenuItem, useUniqueId as useUniqueId2, areRowsFocused, isRowFirst } from "@dropbox/dig-components/hooks";

// ../src/typeahead/row_accessory.tsx
import classNames from "classnames";
import * as React3 from "react";

// ../src/typeahead/index.css.ts
import { createRuntimeFn as _7a468 } from "@vanilla-extract/recipes/createRuntimeFn";
var rowHighlight = "dig-lwfqqf6_21-7-0";
var showMoreButton = "dig-lwfqqfb_21-7-0";
var typeaheadAccessibleText = "dig-lwfqqfi_21-7-0";
var typeaheadContainer = "dig-lwfqqf0_21-7-0";
var typeaheadContainerCanvas = "dig-lwfqqf1_21-7-0";
var typeaheadContainerOmitOverlayWrapper = "dig-lwfqqf2_21-7-0";
var typeaheadPrompt = "dig-lwfqqf7_21-7-0";
var typeaheadResults = _7a468({ defaultClassName: "dig-lwfqqf3_21-7-0", variantClassNames: { size: { medium: "dig-lwfqqf4_21-7-0", large: "dig-lwfqqf5_21-7-0" } }, defaultVariants: { size: "medium" }, compoundVariants: [] });
var typeaheadResultsTitle = "dig-lwfqqfe_21-7-0";
var typeaheadResultsTitleSpacer = "dig-lwfqqfd_21-7-0";
var typeaheadRow = _7a468({ defaultClassName: "dig-lwfqqf8_21-7-0", variantClassNames: { disabled: { true: "dig-lwfqqf9_21-7-0" }, interactive: { true: "dig-lwfqqfa_21-7-0" } }, defaultVariants: { disabled: false, interactive: false }, compoundVariants: [] });
var typeaheadRowAccessory = "dig-lwfqqfh_21-7-0";
var typeaheadRowContent = "dig-lwfqqfc_21-7-0";
var typeaheadRowSubtitle = "dig-lwfqqfg_21-7-0";
var typeaheadRowTitle = "dig-lwfqqff_21-7-0";

// ../src/typeahead/row_accessory.tsx
var RowAccessory = ({ className, children, ...rest }) => {
  const cls2 = classNames("dig-Typeahead-row-accessory", typeaheadRowAccessory, className);
  return /* @__PURE__ */ React3.createElement("div", { className: cls2, ...rest }, children);
};
RowAccessory.displayName = "RowAccessory";

// ../src/typeahead/row_content.tsx
import classNames2 from "classnames";
import * as React4 from "react";
var RowContent = ({ className, children, ...rest }) => {
  const cls2 = classNames2("dig-Typeahead-row-content", typeaheadRowContent, className);
  return /* @__PURE__ */ React4.createElement("div", { className: cls2, ...rest }, children);
};
RowContent.displayName = "RowContent";

// ../src/typeahead/row_subtitle.tsx
import classNames3 from "classnames";
import * as React5 from "react";
var RowSubtitle = ({ className, children, ...rest }) => {
  const cls2 = classNames3("dig-Typeahead-row-subtitle", typeaheadRowSubtitle, className);
  return /* @__PURE__ */ React5.createElement("div", { className: cls2, ...rest }, children);
};
RowSubtitle.displayName = "RowSubtitle";

// ../src/typeahead/row_title.tsx
import classNames4 from "classnames";
import * as React6 from "react";
var RowTitle = ({ className, children, ...rest }) => {
  const cls2 = classNames4("dig-Typeahead-row-title", typeaheadRowTitle, className);
  return /* @__PURE__ */ React6.createElement("div", { className: cls2, ...rest }, children);
};
RowTitle.displayName = "RowTitle";

// ../src/typeahead/row.tsx
var AccessibleText = ({ id, title, subTitle, sectionTitle }) => {
  return /* @__PURE__ */ React7.createElement("div", { className: classNames5("dig-Typeahead-accessible-text", typeaheadAccessibleText), id }, title, " ", subTitle, sectionTitle && `, ${sectionTitle}`);
};
var Row = React7.forwardRef(
  ({
    tagName: TagName = "li",
    role = "option",
    disabled = false,
    interactive = true,
    className,
    withLeftAccessory,
    withRightAccessory,
    withSubtitle,
    withTitle,
    onFocus,
    onClick,
    onKeyDown,
    value,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tabIndex = -1,
    children,
    ...rest
  }, ref) => {
    const localRef = React7.useRef(null);
    const { menuRows, setSelected, setFocused, shouldHighlightFirstRow } = React7.useContext(TypeaheadContext);
    const { resultIndex } = React7.useContext(TypeaheadResultContext);
    const { rowIndex, focusIndex, sectionTitle } = React7.useContext(TypeaheadRowContext);
    const index = 100 * (resultIndex || 0) + (rowIndex || 0);
    const id = useUniqueId2({ prefix: "" });
    const { getItemProps } = useMenuItem(
      { interactive, disabled, ref: localRef, role, index, itemValue: value },
      menuRows
    );
    const [isFocused, setIsFocused] = React7.useState(false);
    const [isFirstRow, setIsFirstRow] = React7.useState(false);
    React7.useEffect(() => {
      if (isRowFirst(menuRows, index)) {
        setIsFirstRow(true);
      }
      if (isFirstRow && areRowsFocused(menuRows)) {
        setIsFocused(true);
      } else {
        setIsFocused(false);
      }
    }, [menuRows, index, rowIndex, isFirstRow]);
    React7.useEffect(() => {
      if (focusIndex !== void 0 && focusIndex === rowIndex) {
        setTimeout(() => {
          if (!localRef.current) return;
          localRef.current.focus();
        }, 0);
      }
    }, [focusIndex, rowIndex]);
    const cls2 = classNames5(
      "dig-Typeahead-row",
      {
        "dig-Typeahead-row--disabled": disabled,
        "dig-Typeahead-row--interactive": interactive
      },
      typeaheadRow({ disabled, interactive }),
      className
    );
    const highlightCls = classNames5(
      cls2,
      {
        "dig-Typeahead-row--highlight": shouldHighlightFirstRow,
        [rowHighlight]: shouldHighlightFirstRow
      },
      className
    );
    React7.useImperativeHandle(ref, () => ({
      // Used by `floating-ui`
      getBoundingClientRect: () => {
        if (!localRef.current) return;
        return localRef.current.getBoundingClientRect();
      }
    }));
    const handleClick = (evt) => {
      evt.preventDefault();
      setSelected(value, evt);
      setFocused(void 0);
      if (onClick) {
        onClick(evt);
      }
    };
    const handleFocus = (evt) => {
      setFocused(value, rowIndex);
      if (onFocus) {
        onFocus(evt);
      }
    };
    const handleKeyDown = (evt) => {
      if (onKeyDown && !disabled) {
        onKeyDown(evt);
      }
    };
    return /* @__PURE__ */ React7.createElement(
      TagName,
      {
        onClick: handleClick,
        onFocus: handleFocus,
        onKeyDown: handleKeyDown,
        className: isFirstRow && !isFocused ? highlightCls : cls2,
        ref: localRef,
        ...getItemProps({ ...rest })
      },
      children ? children : /* @__PURE__ */ React7.createElement(React7.Fragment, null, withLeftAccessory && /* @__PURE__ */ React7.createElement(RowAccessory, null, withLeftAccessory), /* @__PURE__ */ React7.createElement(RowContent, { "aria-labelledby": id }, /* @__PURE__ */ React7.createElement(RowTitle, { "aria-hidden": "true" }, withTitle), withSubtitle && /* @__PURE__ */ React7.createElement(RowSubtitle, { "aria-hidden": "true" }, withSubtitle)), /* @__PURE__ */ React7.createElement(
        AccessibleText,
        {
          id,
          title: withTitle,
          subTitle: withSubtitle,
          sectionTitle
        }
      ), withRightAccessory && /* @__PURE__ */ React7.createElement(RowAccessory, null, withRightAccessory))
    );
  }
);
Row.displayName = "Row";

// ../src/typeahead/container.tsx
import * as React9 from "react";
import { ClickOutside as ClickOutside2 } from "@dropbox/dig-components/click_outside";
import { useMaxHeight } from "@dropbox/dig-components/hooks";
import { Overlay } from "@dropbox/dig-components/overlay";
import { Spinner } from "@dropbox/dig-components/progress_indicators";

// ../src/typeahead/prompt.tsx
import * as React8 from "react";
import classNames6 from "classnames";
var Prompt = ({ children }) => {
  return /* @__PURE__ */ React8.createElement("div", { className: classNames6("dig-Typeahead-prompt", typeaheadPrompt) }, children);
};
Prompt.displayName = "Prompt";

// ../src/typeahead/container.tsx
import cls from "classnames";
var Container = ({
  open,
  triggerRef,
  triggerOffset = 4,
  emptyPrompt,
  isEmptyQuery = false,
  loading,
  children,
  auto = true,
  positioningStrategy = "absolute",
  placement = "bottom-start",
  omitOverlay = false,
  className,
  ...rest
}) => {
  const {
    menuRows,
    closeMenu,
    contentRef,
    isPortaled,
    containerId,
    inlineProps,
    hasMaxHeight,
    shouldOmitWrapperOverlay,
    setShouldOmitWrapperOverlay,
    setIsTypeaheadDropdownVisible,
    setSyntheticFocus
  } = React9.useContext(TypeaheadContext);
  const { onKeyDownCapture } = rest;
  const handleClickOutside = React9.useCallback(() => {
    closeMenu({ autoFocus: false });
  }, [closeMenu]);
  const { styles: maxHeightStyles } = useMaxHeight({ hasMaxHeight, contentRef });
  const hasEmptyPrompt = typeof emptyPrompt !== "undefined" && emptyPrompt !== null;
  React9.useEffect(() => {
    let isTypeaheadVisible = true;
    if (!loading && !(isEmptyQuery && hasEmptyPrompt) && menuRows.items.size === 0) {
      isTypeaheadVisible = false;
    }
    setIsTypeaheadDropdownVisible(isTypeaheadVisible);
  }, [loading, hasEmptyPrompt, isEmptyQuery, menuRows.items.size, setIsTypeaheadDropdownVisible]);
  React9.useEffect(() => {
    if (omitOverlay) {
      setShouldOmitWrapperOverlay(true);
    }
  }, [omitOverlay, setShouldOmitWrapperOverlay]);
  const onOmitOverlayKeyDown = (e) => {
    if (
      // NOTE: we don't care if this is shift+tab or tab, we go back to the trigger element
      e.key === "Tab" && triggerRef.current && "focus" in triggerRef.current && typeof triggerRef.current.focus === "function"
    ) {
      triggerRef.current.focus();
      e.preventDefault();
    }
    if (onKeyDownCapture) {
      onKeyDownCapture(e);
    }
  };
  if (!open || !children) {
    return null;
  }
  let content = void 0;
  let displayStyles = {};
  if (loading) {
    content = /* @__PURE__ */ React9.createElement(Prompt, null, /* @__PURE__ */ React9.createElement(Spinner, null));
  } else if (isEmptyQuery && hasEmptyPrompt) {
    content = emptyPrompt;
  } else if (menuRows.items.size === 0) {
    content = children;
    displayStyles = { display: "none" };
  } else {
    content = React9.Children.map(children, (child, i) => {
      return /* @__PURE__ */ React9.createElement(TypeaheadResultContext.Provider, { value: { resultIndex: i } }, child);
    });
  }
  if (shouldOmitWrapperOverlay) {
    return /* @__PURE__ */ React9.createElement(
      "ul",
      {
        id: containerId,
        style: displayStyles,
        ...rest,
        onKeyDownCapture: onOmitOverlayKeyDown,
        onPointerDownCapture: () => {
          setSyntheticFocus();
        },
        className: cls(
          "dig-Typeahead-container__omit-overlay-results-wrapper",
          typeaheadContainerOmitOverlayWrapper
        )
      },
      content
    );
  }
  const containerClassName = cls("dig-Typeahead-container", typeaheadContainer, className);
  const container = open ? /* @__PURE__ */ React9.createElement(
    Overlay,
    {
      anchorRef: triggerRef,
      auto,
      isPortaled,
      placement,
      positioningStrategy,
      setWidthSameAsAnchor: !inlineProps,
      offsetDistance: triggerOffset
    },
    /* @__PURE__ */ React9.createElement(
      "ul",
      {
        className: containerClassName,
        ref: contentRef,
        id: containerId,
        style: displayStyles,
        ...rest
      },
      /* @__PURE__ */ React9.createElement(
        "div",
        {
          className: cls("dig-Typeahead-container--canvas", typeaheadContainerCanvas),
          style: maxHeightStyles,
          onMouseDownCapture: () => setSyntheticFocus()
        },
        content
      )
    )
  ) : null;
  return inlineProps ? /* @__PURE__ */ React9.createElement(
    ClickOutside2,
    {
      className: "dig-Typeahead-backdrop",
      isBlock: true,
      onClickOutside: handleClickOutside,
      shouldPropagateMouseEvents: false,
      isClickThroughPortaled: isPortaled,
      isActive: Boolean(open)
    },
    container
  ) : container;
};
Container.displayName = "Container";

// ../src/typeahead/empty_prompt.tsx
import * as React10 from "react";
import { UIIcon } from "@dropbox/dig-icons";
import { SearchLine } from "@dropbox/dig-icons/assets";
import { Text } from "@dropbox/dig-components/typography";
var EmptyPrompt = (props) => {
  const { placeholderText } = props;
  return /* @__PURE__ */ React10.createElement(Prompt, null, /* @__PURE__ */ React10.createElement(UIIcon, { size: "large", src: SearchLine }), /* @__PURE__ */ React10.createElement(Text, { color: "subtle" }, placeholderText));
};
EmptyPrompt.displayName = "EmptyPrompt";

// ../src/typeahead/results.tsx
import classNames7 from "classnames";
import * as React12 from "react";

// ../src/typeahead/show_more.tsx
import * as React11 from "react";
import { Button } from "@dropbox/dig-components/buttons";
import { useMenuItem as useMenuItem2 } from "@dropbox/dig-components/hooks";
import cx from "classnames";
var ShowMore = ({
  onShowMoreClick,
  showMoreButtonText = "Show more"
}) => {
  const localRef = React11.useRef(null);
  const { menuRows, setFocused } = React11.useContext(TypeaheadContext);
  const { resultIndex } = React11.useContext(TypeaheadResultContext);
  const { rowIndex } = React11.useContext(TypeaheadRowContext);
  const index = 100 * (resultIndex || 0) + (rowIndex || 0);
  const { getItemProps } = useMenuItem2(
    { interactive: true, disabled: true, ref: localRef, index },
    menuRows
  );
  const itemProps = getItemProps();
  if ("role" in itemProps) {
    delete itemProps.role;
  }
  const handleClick = (evt) => {
    evt.stopPropagation();
    evt.preventDefault();
    onShowMoreClick();
  };
  const handleKeyDown = (evt) => {
    if (evt.key === ENTER_KEY || evt.key === SPACEBAR_KEY) {
      evt.stopPropagation();
      evt.preventDefault();
      onShowMoreClick();
    }
  };
  const handleFocus = () => {
    setFocused(void 0);
  };
  return /* @__PURE__ */ React11.createElement(
    Button,
    {
      className: cx("dig-Typeahead-results--show-more", showMoreButton),
      variant: "transparent",
      ref: localRef,
      type: "button",
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      onMouseDown: handleClick,
      onFocus: handleFocus,
      ...itemProps,
      disabled: false,
      "aria-disabled": false
    },
    showMoreButtonText
  );
};
ShowMore.displayName = "ShowMore";

// ../src/typeahead/results.tsx
import { Text as Text2 } from "@dropbox/dig-components/typography";

// ../src/_global/sizes.tsx
var deprecationMap = [
  // [<modern-value>, <legacy-value>]
  ["medium", "standard"]
];
var getMigratedComponentSize = (size) => {
  const mapping = deprecationMap.find(([_newVal, deprecatedVal]) => size === deprecatedVal);
  if (mapping !== void 0) {
    return mapping[0];
  }
  return size;
};

// ../src/typeahead/results.tsx
function Results({
  results,
  title,
  renderRow,
  initialResults = 4,
  maxResults = 8,
  showMoreButtonText,
  size: _size = "medium"
}) {
  const { setFocused } = React12.useContext(TypeaheadContext);
  const size = getMigratedComponentSize(_size);
  const [numberOfResultsDisplayed, setNumberOfResultsDisplayed] = React12.useState(
    Math.min(results.length, initialResults)
  );
  const [focusIndexAfterShowMore, setFocusIndexAfterShowMore] = React12.useState();
  React12.useEffect(() => {
    setNumberOfResultsDisplayed(Math.min(results.length, initialResults));
  }, [results, initialResults]);
  if (!results || results.length === 0) {
    return null;
  }
  const cls2 = classNames7(
    "dig-Typeahead-results",
    {
      "dig-Typeahead-results--standard": size === "medium",
      "dig-Typeahead-results--medium": size === "medium",
      "dig-Typeahead-results--large": size === "large"
    },
    typeaheadResults({ size })
  );
  const onShowMoreClick = () => {
    if (numberOfResultsDisplayed < results.length) {
      setFocused(results[numberOfResultsDisplayed]);
      setFocusIndexAfterShowMore(numberOfResultsDisplayed);
      setNumberOfResultsDisplayed(Math.min(results.length, maxResults));
    }
  };
  return /* @__PURE__ */ React12.createElement("div", { className: cls2 }, title ? /* @__PURE__ */ React12.createElement(
    Text2,
    {
      className: classNames7("dig-Typeahead-results--title", typeaheadResultsTitle),
      color: "subtle",
      isBold: true,
      size: "small",
      "aria-hidden": "true"
    },
    title
  ) : /* @__PURE__ */ React12.createElement(
    "div",
    {
      className: classNames7("dig-Typeahead-results--title-spacer", typeaheadResultsTitleSpacer),
      role: "presentation"
    }
  ), results.slice(0, numberOfResultsDisplayed).map((r, i) => {
    return /* @__PURE__ */ React12.createElement(
      TypeaheadRowContext.Provider,
      {
        key: i,
        value: { rowIndex: i, focusIndex: focusIndexAfterShowMore, sectionTitle: title }
      },
      renderRow(r)
    );
  }), numberOfResultsDisplayed < results.length && numberOfResultsDisplayed !== maxResults && /* @__PURE__ */ React12.createElement(TypeaheadRowContext.Provider, { value: { rowIndex: numberOfResultsDisplayed + 100 } }, /* @__PURE__ */ React12.createElement(ShowMore, { showMoreButtonText, onShowMoreClick })));
}
Results.displayName = "Results";

// ../src/typeahead/typeahead.tsx
var Typeahead = {
  Row,
  RowAccessory,
  RowContent,
  RowSubtitle,
  RowTitle,
  Wrapper,
  Container,
  Prompt,
  EmptyPrompt,
  Results
};
export {
  Typeahead
};
